import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export enum SpecificationType {
	ENVIRONMENT = "ENVIRONMENT",
	CODE_REPO = "CORE_REPO",
	ENTITY = "ENTITY",
	DATABASE = "DATABASE",
	ENV_PROPERTY = "ENV_PROPERTY",
	PRODUCER_API = "PRODUCER_API",
}

export const SpecificationApi = createApi({
	reducerPath: "SpecificationApi",
	baseQuery: fetchBaseQuery({
		baseUrl: import.meta.env.VITE_STUDIO_URL,
		headers: {
			"Content-Type": "application/json",
			"x-api-key": import.meta.env.VITE_STUDIO_KEY,
			accessToken: localStorage.getItem("accessToken") || "",
		},
	}),
	tagTypes: ["specification", "code-specs", "env-specs", "producer-apis"],
	endpoints: (builder) => ({
		getSpecificationDetails: builder.query({
			query: ({ specificationId }) => `/specification/${specificationId}`,
		}),
		getSpecificationOfApplication: builder.query({
			query: ({ applicationId }) => `/specification/application/${applicationId}`,
			providesTags: ["specification"],
		}),
		createSpecification: builder.mutation({
			query: ({
				applicationId,
				type,
				data,
			}: {
				applicationId: string;
				type: SpecificationType;
				data: any;
			}) => ({
				url: `/specification`,
				method: "POST",
				body: {
					application: applicationId,
					type,
					data,
				},
			}),
			invalidatesTags: ["specification"],
		}),
		updateSpecification: builder.mutation({
			query: ({
				specificationId,
				applicationId,
				type,
				data,
			}: {
				specificationId: string;
				applicationId: string;
				type: SpecificationType;
				data: any;
			}) => ({
				url: `/specification/${specificationId}`,
				method: "PATCH",
				body: {
					application: applicationId,
					type,
					data,
				},
			}),
			invalidatesTags: ["specification"],
		}),
		deleteSpecification: builder.mutation({
			query: ({ applicationId }) => ({
				url: `/specification/${applicationId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["specification"],
		}),
		getGitRepos: builder.query({
			query: () => `/git-hub/repos`,
		}),
		//config apis
		getConfigDetailsById: builder.query({
			query: ({ configId }) => `/config/${configId}`,
		}),
		getConfigDetails: builder.query({
			query: () => `/configs`,
		}),
		deleteConfig: builder.mutation({
			query: (configId) => ({
				url: `/config/${configId}`,
				method: "DELETE",
			}),
		}),
		createGitConnection: builder.mutation({
			query: (body) => ({
				url: `/config`,
				method: "POST",
				body,
			}),
		}),
		updateConfig: builder.mutation({
			query: ({ body, configId }) => ({
				url: `/config/${configId}`,
				method: "PATCH",
				body: body,
			}),
		}),
		createCodeSpec: builder.mutation({
			query: (body) => ({
				url: `/code-spec`,
				method: "POST",
				body: body,
			}),
			invalidatesTags: ["code-specs"],
		}),
		getCodeSpecs: builder.query({
			query: (applicationId) => `/code-spec/application/${applicationId}`,
			providesTags: ["code-specs"],
		}),
		deleteCodeSpec: builder.mutation({
			query: (codeSpecId) => ({
				url: `/code-spec/${codeSpecId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["code-specs"],
		}),

		//db-spec
		getDbSpecificationByApplicationId: builder.query({
			query: ({ id, codeSpec, selectedEnv }) =>
				`/db-spec/application/${id}?codeSpec=${codeSpec}&envSpec=${selectedEnv
					.map((env) => env)
					.join(",")}`,
		}),

		// getDbSpecificationFilterCheck: builder.query({
		// 	query: ({ id, codeSpec }) => `/db-spec/application/${id}?codeSpec=${codeSpec}`,
		// }),
		// dbSpecificationCheck: builder.mutation({
		// 	query: (body) => ({
		// 		url: `/test-db-connection`,
		// 		method: 'POST',
		// 		body: body,
		// 	}),
		// }),

		// getDbSpecificationFilterCheck: builder.query({
		// 	query: ({ id, codeSpec }) => `/db-spec/application/${id}?codeSpec=${codeSpec}`,
		// }),
		dbSpecificationCheck: builder.mutation({
			query: (body) => ({
				url: `/test-db-connection`,
				method: "POST",
				body: body,
			}),
		}),

		createDbSpecification: builder.mutation({
			query: (body) => ({
				url: `/db-spec`,
				method: "POST",
				body: body,
			}),
		}),
		updateDbSpecification: builder.mutation({
			query: ({ body, id }) => ({
				url: `/db-spec/${id}`,
				method: "PATCH",
				body: body,
			}),
		}),
		deleteDbSpecification: builder.mutation({
			query: (id) => ({
				url: `/db-spec/${id}`,
				method: "DELETE",
			}),
		}),
		getDbSpecificationDetails: builder.query({
			query: (id) => `/db-spec/${id}`,
		}),

		getDbSpecificationSchema: builder.query({
			query: (id) => `/db-schemas/${id}`,
		}),

		getDbSpecificationSchemaDetails: builder.query({
			query: ({ id, code }) => `/db-schema-details/${id}?tableNameOrCollectionName=${code}`,
		}),
		createEnvSpec: builder.mutation({
			query: (body) => ({
				url: `/env-spec`,
				method: "POST",
				body: body,
			}),
			invalidatesTags: ["env-specs"],
		}),
		getEnvSpecs: builder.query({
			query: (applicationId) => `/env-spec/application/${applicationId}`,
			providesTags: ["env-specs"],
		}),
		deleteEnvSpec: builder.mutation({
			query: (envSpecId) => ({
				url: `/env-spec/${envSpecId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["env-specs"],
		}),
		updateEnvSpec: builder.mutation({
			query: ({ envSpecId, body }) => ({
				url: `/env-spec/${envSpecId}`,
				method: "PATCH",
				body: body,
			}),
			invalidatesTags: ["env-specs"],
		}),
		getProducerApis: builder.query({
			query: ({ applicationId, codeSpecId, envSpecId }) => ({
				url: `/producer-apis/application/${applicationId}`,
				params: {
					codeSpec: codeSpecId,
					envSpec: envSpecId,
				},
			}),
			providesTags: ["producer-apis"],
		}),
		importProducerApis: builder.mutation({
			query: (body) => ({
				url: "/producer-apis",
				method: "POST",
				body,
			}),
			invalidatesTags: ["producer-apis"],
		}),
		testConfigs: builder.mutation({
			query: (body) => ({
				url: `/config/test`,
				method: "POST",
				body,
			}),
		}),
	}),
});

export const {
	useGetSpecificationDetailsQuery,
	useGetSpecificationOfApplicationQuery,
	useCreateSpecificationMutation,
	useUpdateSpecificationMutation,
	useDeleteSpecificationMutation,
	useGetGitReposQuery,
	//config API
	useGetConfigDetailsByIdQuery,
	useGetConfigDetailsQuery,
	useCreateGitConnectionMutation,
	useUpdateConfigMutation,
	useDeleteConfigMutation,
	useCreateCodeSpecMutation,
	useGetCodeSpecsQuery,
	useDeleteCodeSpecMutation,
	//db-spec
	useCreateDbSpecificationMutation,
	useDeleteDbSpecificationMutation,
	useGetDbSpecificationByApplicationIdQuery,
	useDbSpecificationCheckMutation,
	useGetDbSpecificationDetailsQuery,
	useUpdateDbSpecificationMutation,
	useGetDbSpecificationSchemaDetailsQuery,
	useGetDbSpecificationSchemaQuery,
	useCreateEnvSpecMutation,
	useGetEnvSpecsQuery,
	useDeleteEnvSpecMutation,
	useUpdateEnvSpecMutation,
	useGetProducerApisQuery,
	useImportProducerApisMutation,
	useTestConfigsMutation,
} = SpecificationApi;
